export const sortEventsByStartData = (events) => {
    try {
        return events.sort((a, b) => new Date(a.start_date).getTime() > new Date(b.start_date).getTime() ? 1 : -1)
    } catch (e) {
        console.log("sort error! sortEventsByStartData");
        console.log(e);
        return events
    }
}


const compareTimeAscending = (timeA, timeB) => timeA.getTime() - timeB.getTime();
const compareTimeDescending = (timeA, timeB) => timeB.getTime() - timeA.getTime();

/**
 * Sorts events by start_hour (time) within the same date.
 * Assumes events are already sorted by start_date from the API.
 * Note: start_hour is stored as a full ISO datetime string, not just a time.
 *
 * @param {Array} events - Array of event objects (pre-sorted by date from API)
 * @param {'asc'|'desc'} order - Sort order: 'asc' (earlier times first) or 'desc' (later times first, default)
 * @returns {Array} Array with events on the same date sorted by time
 *
 * Sorting behavior:
 * - Events on different dates: maintains original API order
 * - Events on the same date WITH start_hour: sorted by time according to order param
 * - Events on the same date WITHOUT start_hour: placed before events with start_hour
 * - Example (desc): For same date, 18:30 appears before 17:30, which appears before 12:00
 * - Example (asc): For same date, 12:00 appears before 17:30, which appears before 18:30
 */
export const sortEventsByStartHour = (events, order = 'desc') => {
    try {
        if (!events?.length) {
            return [];
        }

        // Default to descending (later times first)
        let compareTime = compareTimeDescending;
        if (order === 'asc') {
            compareTime = compareTimeAscending;
        }

        // Preserve the API's day order via first-seen index, then sort by start_hour WITHIN
        // each day. The previous comparator returned 0 for events on different dates while
        // ordering same-date events by hour, which is not a valid strict-weak-ordering:
        // Array.prototype.sort then produced engine-/input-dependent results and the per-day
        // hour ordering was effectively dropped (the "hour ignored" bug). Decorating with the
        // original index makes this a stable, valid total order without assuming the input is
        // already grouped by date. Day key is normalized so "2026-06-10" and
        // "2026-06-10T00:00:00Z" group together.
        const dayKeyOf = (event) => {
            const d = new Date(event?.start_date);
            return isNaN(d.getTime()) ? String(event?.start_date ?? '') : d.toISOString().slice(0, 10);
        };

        const dayOrder = new Map();
        events.forEach((event) => {
            const key = dayKeyOf(event);
            if (!dayOrder.has(key)) dayOrder.set(key, dayOrder.size);
        });

        return events
            .map((event, index) => ({ event, index }))
            .sort((a, b) => {
                // Primary: keep the API's day order
                const dayA = dayOrder.get(dayKeyOf(a.event));
                const dayB = dayOrder.get(dayKeyOf(b.event));
                if (dayA !== dayB) return dayA - dayB;

                // Within the same day: events without an hour come first
                const aHasHour = !!a.event.start_hour;
                const bHasHour = !!b.event.start_hour;
                if (aHasHour !== bHasHour) return aHasHour ? 1 : -1;
                if (!aHasHour) return a.index - b.index;

                // Both have an hour: order by time, stable on ties
                const cmp = compareTime(new Date(a.event.start_hour), new Date(b.event.start_hour));
                return cmp !== 0 ? cmp : a.index - b.index;
            })
            .map(({ event }) => event);
    } catch (e) {
        console.log("sort error! sortEventsByStartHour");
        console.log(e);
        return events;
    }
}

export const isFutureEvent = (event) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0);
    const eventStateDate = new Date(event.end_date ? event.end_date : event.start_date)
    eventStateDate.setHours(0, 0, 0, 0);
    return eventStateDate.getTime() >= now.getTime()
}


export const isPastEvent = (event) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0);
    const eventStateDate = new Date(event.start_date)
    eventStateDate.setHours(0, 0, 0, 0);
    return eventStateDate.getTime() < now.getTime()
}
